import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { useToast } from '@/hooks/use-toast';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Loader2, AlertCircle, History, RefreshCw } from 'lucide-react';
import { captureError } from '@/lib/sentry';
import type { MergeHistoryItem } from '@/types/merge';

interface MergeHistoryProps {
  workspaceId: string;
  refreshTrigger?: number;
}

export function MergeHistory({ workspaceId, refreshTrigger = 0 }: MergeHistoryProps) {
  const [history, setHistory] = useState<MergeHistoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [undoingMerge, setUndoingMerge] = useState<string | null>(null);
  const { toast } = useToast();

  useEffect(() => {
    const fetchMergeHistory = async () => {
      if (!workspaceId) return;
      
      try {
        setLoading(true);
        setError(null);
        
        // Fetch merge history filtered by workspace_id (column now exists with RLS)
        const { data, error } = await supabase
          .from('merge_history')
          .select('*')
          .eq('workspace_id', workspaceId)
          .order('merge_timestamp', { ascending: false });

        if (error) throw error;

        setHistory(data || []);
      } catch (err) {
        console.error('Error fetching merge history:', err);
        setError('Failed to load merge history. Please try again.');
        captureError(err as Error, { feature: 'duplicates-merge-history-fetch' });
        toast({
          title: "Error",
          description: "Failed to load merge history",
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    };
    
    fetchMergeHistory();
  }, [workspaceId, refreshTrigger, toast]);

  const handleUndo = async (mergeId: string) => {
    try {
      setUndoingMerge(mergeId);
      
      const { error } = await supabase.rpc('undo_merge_v2', { merge_id: mergeId });
      
      if (error) throw error;
      
      toast({
        title: "Success",
        description: "Merge has been successfully undone",
      });
      
      // Update the merge history item to reflect the change
      setHistory(prevHistory => 
        prevHistory.map(item => 
          item.id === mergeId ? { ...item, undo_status: 'undone' } : item
        )
      );
    } catch (error: any) {
      console.error('Error undoing merge:', error);
      captureError(error as Error, { feature: 'duplicates-merge-history-handle-undo' });
      toast({
        title: "Error",
        description: error?.message || "Failed to undo the merge",
        variant: "destructive",
      });
    } finally {
      setUndoingMerge(null);
    }
  };

  // Format the display name for a contact
  const getContactName = (contact: any) => {
    if (!contact) return 'Unknown Contact';
    
    if (contact.is_company) {
      return contact.company_name || 'Unnamed Company';
    }
    
    return `${contact.first_name || ''} ${contact.last_name || ''}`.trim() || 'Unnamed Contact';
  };

  // Check if a merge can be undone
  const canUndo = (historyItem: MergeHistoryItem) => {
    return historyItem.undo_status === 'available' && new Date(historyItem.undo_expiry) > new Date();
  };

  // Format expiry date for display
  const formatExpiry = (expiryDate: string) => {
    const expiry = new Date(expiryDate);
    const now = new Date();
    
    if (expiry < now) {
      return 'Expired';
    }
    
    const diffTime = Math.abs(expiry.getTime() - now.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    return `Expires in ${diffDays} day${diffDays !== 1 ? 's' : ''}`;
  };

  // Display loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center space-y-4">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-muted-foreground">Loading merge history...</p>
        </div>
      </div>
    );
  }

  // Display error state
  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center space-y-4">
          <AlertCircle className="h-8 w-8 text-destructive" />
          <p className="text-destructive">{error}</p>
          <Button onClick={() => window.location.reload()}>
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  // Display empty state
  if (history.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center space-y-4">
          <History className="h-12 w-12 text-muted-foreground mx-auto" />
          <div>
            <p className="text-muted-foreground">No merge history found.</p>
            <p className="text-sm text-muted-foreground">
              When you merge contacts, the history will appear here.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Kept Contact</TableHead>
              <TableHead>Discarded Contact</TableHead>
              <TableHead>Merge Date</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {history.map((item) => (
              <TableRow key={item.id}>
                <TableCell className="font-medium">{getContactName(item.keep_data)}</TableCell>
                <TableCell>{getContactName(item.discard_data)}</TableCell>
                <TableCell>
                  {new Date(item.merge_timestamp).toLocaleDateString(undefined, {
                    year: 'numeric', 
                    month: 'short', 
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </TableCell>
                <TableCell>
                  <Badge 
                    variant={
                      item.undo_status === 'available' ? "default" : 
                      item.undo_status === 'undone' ? "outline" : 
                      "secondary"
                    }
                  >
                    {item.undo_status === 'available' ? 
                      formatExpiry(item.undo_expiry) : 
                      item.undo_status === 'undone' ? 
                        'Undone' : 'Expired'}
                  </Badge>
                </TableCell>
                <TableCell className="text-right">
                  {canUndo(item) ? (
                    <Button
                      variant="outline" 
                      size="sm" 
                      onClick={() => handleUndo(item.id)}
                      disabled={undoingMerge === item.id}
                    >
                      {undoingMerge === item.id ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Undoing...
                        </>
                      ) : (
                        <>
                          <RefreshCw className="mr-2 h-4 w-4" />
                          Undo Merge
                        </>
                      )}
                    </Button>
                  ) : (
                    <span className="text-sm text-muted-foreground">
                      {item.undo_status === 'undone' ? 'Already undone' : 'Cannot undo'}
                    </span>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
